import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Search,
  Euro,
  Percent,
  Box,
  Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierPrices, setSupplierPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProductForPrice, setSelectedProductForPrice] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    image_url: "",
    vat_rate: "19",
    sale_price: "",
    deposit: "0",
    min_stock: "10"
  });

  // Price form state
  const [priceFormData, setPriceFormData] = useState({
    supplier_id: "",
    article_number: "",
    package_size: "1",
    package_price_net: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`)
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);

      // Fetch supplier prices for all products
      const pricesRes = await axios.get(`${API}/supplier-prices`);
      const pricesByProduct = {};
      pricesRes.data.forEach(price => {
        if (!pricesByProduct[price.product_id]) {
          pricesByProduct[price.product_id] = [];
        }
        pricesByProduct[price.product_id].push(price);
      });
      setSupplierPrices(pricesByProduct);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      category: "",
      image_url: "",
      vat_rate: "19",
      sale_price: "",
      deposit: "0",
      min_stock: "10"
    });
    setDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || "",
      category: product.category || "",
      image_url: product.image_url || "",
      vat_rate: String(product.vat_rate || 19),
      sale_price: String(product.sale_price || ""),
      deposit: String(product.deposit || 0),
      min_stock: String(product.min_stock || 10)
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Bitte Produktname eingeben");
      return;
    }

    try {
      const data = {
        name: formData.name,
        category: formData.category || null,
        image_url: formData.image_url || null,
        vat_rate: parseFloat(formData.vat_rate) || 19,
        sale_price: parseFloat(formData.sale_price) || 0,
        deposit: parseFloat(formData.deposit) || 0,
        min_stock: parseInt(formData.min_stock) || 10
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success("Produkt aktualisiert");
      } else {
        await axios.post(`${API}/products`, data);
        toast.success("Produkt erstellt");
      }

      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async () => {
    if (!editingProduct) return;

    try {
      await axios.delete(`${API}/products/${editingProduct.id}`);
      toast.success("Produkt gelöscht");
      setDeleteDialogOpen(false);
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Supplier Price functions
  const openPriceDialog = (product) => {
    setSelectedProductForPrice(product);
    setPriceFormData({
      supplier_id: "",
      article_number: "",
      package_size: "1",
      package_price_net: ""
    });
    setPriceDialogOpen(true);
  };

  const handlePriceSubmit = async () => {
    if (!priceFormData.supplier_id) {
      toast.error("Bitte Lieferant auswählen");
      return;
    }

    try {
      await axios.post(`${API}/supplier-prices`, {
        product_id: selectedProductForPrice.id,
        supplier_id: priceFormData.supplier_id,
        article_number: priceFormData.article_number || null,
        package_size: parseInt(priceFormData.package_size) || 1,
        package_price_net: parseFloat(priceFormData.package_price_net) || 0
      });

      toast.success("Lieferantenpreis gespeichert");
      setPriceDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const deleteSupplierPrice = async (priceId) => {
    try {
      await axios.delete(`${API}/supplier-prices/${priceId}`);
      toast.success("Lieferantenpreis gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Produkte
          </h1>
          <p className="text-slate-500 mt-1">
            {products.length} Produkte im Sortiment
          </p>
        </div>
        <Button
          onClick={openNewProduct}
          className="bg-rose-600 hover:bg-rose-700"
          data-testid="new-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neues Produkt
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Produkte suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <Card className="border-slate-200 hover:shadow-md transition-shadow">
                {/* Product Image */}
                <div className="h-32 bg-slate-100 rounded-t-lg flex items-center justify-center overflow-hidden">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-12 h-12 text-slate-300" />
                  )}
                </div>

                <CardContent className="pt-4">
                  {/* Name & Category */}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{product.name}</h3>
                      {product.category && (
                        <Badge variant="outline" className="mt-1">
                          {product.category}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditProduct(product)}
                      data-testid="edit-product-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Price Info */}
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Verkaufspreis:</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {product.sale_price?.toFixed(2) || "0.00"}€
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">MwSt:</span>
                      <span>{product.vat_rate || 19}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Min. Bestand:</span>
                      <span>{product.min_stock || 10} Stk.</span>
                    </div>
                  </div>

                  {/* Supplier Prices */}
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-slate-500">EINKAUFSPREISE</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => openPriceDialog(product)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Hinzufügen
                      </Button>
                    </div>
                    
                    {supplierPrices[product.id]?.length > 0 ? (
                      <div className="space-y-1">
                        {supplierPrices[product.id].map((price) => (
                          <div
                            key={price.id}
                            className="flex justify-between items-center text-xs bg-slate-50 rounded px-2 py-1"
                          >
                            <div>
                              <span className="font-medium">{price.supplier_name}</span>
                              {price.article_number && (
                                <span className="text-slate-400 ml-1">
                                  ({price.article_number})
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">
                                {price.unit_price_net?.toFixed(2)}€
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-slate-400 hover:text-red-500"
                                onClick={() => deleteSupplierPrice(price.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 text-center py-2">
                        Keine Einkaufspreise
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Produkt bearbeiten" : "Neues Produkt"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Produktname *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Cola 0,5l"
              />
            </div>

            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="z.B. Getränke"
              />
            </div>

            <div className="space-y-2">
              <Label>Bild-URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Verkaufspreis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  placeholder="1.50"
                />
              </div>

              <div className="space-y-2">
                <Label>MwSt-Satz</Label>
                <Select
                  value={formData.vat_rate}
                  onValueChange={(v) => setFormData({ ...formData, vat_rate: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="19">19% (Standard)</SelectItem>
                    <SelectItem value="7">7% (ermäßigt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pfand (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.deposit}
                  onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                  placeholder="0.25"
                />
              </div>

              <div className="space-y-2">
                <Label>Mindestbestand</Label>
                <Input
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            {editingProduct && (
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSubmit} className="bg-rose-600 hover:bg-rose-700">
                {editingProduct ? "Speichern" : "Erstellen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier Price Dialog */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Einkaufspreis hinzufügen
            </DialogTitle>
          </DialogHeader>

          {selectedProductForPrice && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-500">Produkt:</p>
                <p className="font-medium">{selectedProductForPrice.name}</p>
              </div>

              <div className="space-y-2">
                <Label>Lieferant *</Label>
                <Select
                  value={priceFormData.supplier_id}
                  onValueChange={(v) => setPriceFormData({ ...priceFormData, supplier_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lieferant wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Artikelnummer beim Lieferant</Label>
                <Input
                  value={priceFormData.article_number}
                  onChange={(e) => setPriceFormData({ ...priceFormData, article_number: e.target.value })}
                  placeholder="z.B. 12345"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>VPE-Größe (Stück)</Label>
                  <Input
                    type="number"
                    value={priceFormData.package_size}
                    onChange={(e) => setPriceFormData({ ...priceFormData, package_size: e.target.value })}
                    placeholder="24"
                  />
                </div>

                <div className="space-y-2">
                  <Label>VPE-Preis Netto (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={priceFormData.package_price_net}
                    onChange={(e) => setPriceFormData({ ...priceFormData, package_price_net: e.target.value })}
                    placeholder="12.50"
                  />
                </div>
              </div>

              {priceFormData.package_size && priceFormData.package_price_net && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <p className="text-blue-600">Berechneter Stückpreis:</p>
                  <p className="font-mono font-bold text-lg">
                    {(parseFloat(priceFormData.package_price_net) / parseInt(priceFormData.package_size) || 0).toFixed(4)}€ netto
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handlePriceSubmit} className="bg-rose-600 hover:bg-rose-700">
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{editingProduct?.name}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
